import { Component, OnInit } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { SharedataService } from 'src/app/common/service/sharedata.service';

@Component({
  selector: 'app-theme-customizer',
  templateUrl: './theme-customizer.component.html',
  styleUrls: ['./theme-customizer.component.scss'],
})
export class ThemeCustomizerComponent implements OnInit {
  selectedTheme = 'light-theme';
  sidebarColors = [
    'sidebarcolor1',
    'sidebarcolor2',
    'sidebarcolor3',
    'sidebarcolor4',
    'sidebarcolor5',
    'sidebarcolor6',
    'sidebarcolor7',
    'sidebarcolor8',
  ];
  headerColors = [
    'headercolor1',
    'headercolor2',
    'headercolor3',
    'headercolor4',
    'headercolor5',
    'headercolor6',
    'headercolor7',
    'headercolor8',
  ];
  public switcherOpen = false;

  private readonly THEME_CLASSES = ['light-theme', 'dark-theme', 'semi-dark'];

  constructor(
    private shareService: SharedataService,
    private overlayContainer: OverlayContainer,
  ) {
    // Subscribe in constructor so theme updates from app.component's applyTheme()
    // are reflected in this component's radio buttons immediately.
    this.shareService.getSharedData().subscribe({
      next: (data) => {
        if (data['theme']) {
          this.selectedTheme = data['theme'];
        }
      },
      error: (err) => console.error('Error:', err),
    });
  }

  ngOnInit() {
    const html = document.documentElement;
    const overlay = this.overlayContainer.getContainerElement();

    // ─── FIXED ──────────────────────────────────────────────────────────────
    // This is the ONLY place that writes the theme to the DOM on startup.
    // AppComponent.ngOnInit() runs first (parent before child) but only reads
    // localStorage — it never touches <html>. So by the time we get here, the
    // DOM is still clean and we apply the correct class exactly once.
    // ────────────────────────────────────────────────────────────────────────

    // First-ever visit: stamp the flag and default to light-theme
    if (!localStorage.getItem('themeInitialized')) {
      localStorage.setItem('selectedTheme', 'light-theme');
      localStorage.setItem('themeInitialized', 'true');
    }

    const savedTheme = localStorage.getItem('selectedTheme') || 'light-theme';
    this.selectedTheme = savedTheme;

    // Clear any stale theme classes then apply the correct one
    this.THEME_CLASSES.forEach((cls) => {
      html.classList.remove(cls);
      overlay.classList.remove(cls);
    });
    html.classList.add(savedTheme);
    overlay.classList.add(savedTheme);

    // Broadcast so AppComponent's subscription updates selectedTheme to match.
    // BehaviorSubject replays this to any subscriber that registers later.
    this.shareService.updateSharedData('theme', savedTheme);

    // Restore saved sidebar color ON TOP of the already-applied theme class.
    // loadStoredSidebarColor uses classList (additive) so the theme is preserved.
    // this.loadStoredSidebarColor();
  }

  toggleSwitcher() {
    this.switcherOpen = !this.switcherOpen;
  }

  // Called by the theme-picker radio buttons in the template
  applyTheme() {
    const html = document.documentElement;
    const overlay = this.overlayContainer.getContainerElement();

    this.THEME_CLASSES.forEach((cls) => {
      html.classList.remove(cls);
      overlay.classList.remove(cls);
    });
    html.classList.add(this.selectedTheme);
    overlay.classList.add(this.selectedTheme);

    localStorage.setItem('selectedTheme', this.selectedTheme);
    localStorage.removeItem('selectedSidebarColor');

    this.shareService.updateSharedData('theme', this.selectedTheme);
  }

  applySidebarColor(color: string) {
    const index = this.sidebarColors.indexOf(color);
    const headerColor = this.headerColors[index];
    localStorage.setItem('selectedSidebarColor', this.sidebarColors[index]);

    const html = document.documentElement;
    const currentTheme = this.THEME_CLASSES.find((cls) =>
      html.classList.contains(cls),
    );

    html.setAttribute(
      'class',
      `color-header color-sidebar ${color} ${headerColor}${currentTheme ? ' ' + currentTheme : ''}`,
    );
  }

  loadStoredSidebarColor() {
    const storedColor = localStorage.getItem('selectedSidebarColor');
    if (storedColor) {
      const index = this.sidebarColors.indexOf(storedColor);
      const headerColor = this.headerColors[index];
      const html = document.documentElement;
      const currentTheme = this.THEME_CLASSES.find((cls) =>
        html.classList.contains(cls),
      );
      html.setAttribute(
        'class',
        `color-header color-sidebar ${storedColor} ${headerColor}${currentTheme ? ' ' + currentTheme : ''}`,
      );
    }
  }

  applyHeaderColor(color: string) {
    const html = document.documentElement;
    const currentTheme = this.THEME_CLASSES.find((cls) =>
      html.classList.contains(cls),
    );
    html.setAttribute(
      'class',
      `color-header ${color}${currentTheme ? ' ' + currentTheme : ''}`,
    );
  }
}